// BackgroundProvider — swaps the rear-most background layer per major menu
// section. Reads the user's per-section mapping from localStorage (falls
// back to defaults defined in config/backgrounds.js).
//
// How it works:
//  - Sets a CSS variable `--app-background` on <html>.
//  - `body::before` (defined in index.css) uses that variable as its layer.
//  - Route changes recompute the current section and swap the value.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  BACKGROUNDS, BACKGROUNDS_BY_ID, BASE_COLOR, DEFAULT_SECTION_BG,
  SECTIONS, THEME_PRESETS, PRESETS_BY_ID, sectionKeyFromPath,
} from "../config/backgrounds";

const STORAGE_KEY = "talmia.backgrounds.v1";
const PRESET_KEY  = "talmia.backgrounds.preset.v1";

const BackgroundContext = createContext(null);

function loadMap() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SECTION_BG };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_SECTION_BG, ...parsed };
  } catch { return { ...DEFAULT_SECTION_BG }; }
}
function saveMap(map) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}
function loadPreset() {
  try { return localStorage.getItem(PRESET_KEY) || null; } catch { return null; }
}
function savePreset(id) {
  try {
    if (id) localStorage.setItem(PRESET_KEY, id);
    else    localStorage.removeItem(PRESET_KEY);
  } catch { /* ignore */ }
}

function applyBg(bgId) {
  const bg = BACKGROUNDS_BY_ID[bgId] || BACKGROUNDS[0];
  document.documentElement.style.setProperty("--app-background", bg.css);
  document.documentElement.style.setProperty("--app-background-base", BASE_COLOR);
  document.documentElement.setAttribute("data-bg", bgId);
}

export function BackgroundProvider({ children }) {
  const location = useLocation();
  const [map, setMap] = useState(loadMap);
  const [activePresetId, setActivePresetId] = useState(loadPreset);

  const sectionKey = useMemo(() => sectionKeyFromPath(location.pathname), [location.pathname]);
  const currentBgId = map[sectionKey] || DEFAULT_SECTION_BG[sectionKey] || BACKGROUNDS[0].id;

  useEffect(() => { applyBg(currentBgId); }, [currentBgId]);

  const setSectionBg = useCallback((sec, bgId) => {
    setMap((prev) => {
      const next = { ...prev, [sec]: bgId };
      saveMap(next);
      return next;
    });
    // Any manual section pick invalidates the "single-preset" badge.
    setActivePresetId(null);
    savePreset(null);
  }, []);

  const resetDefaults = useCallback(() => {
    saveMap({});
    setMap({ ...DEFAULT_SECTION_BG });
    setActivePresetId(null);
    savePreset(null);
  }, []);

  const applyPreset = useCallback((presetId) => {
    const preset = PRESETS_BY_ID[presetId];
    if (!preset) return;
    const next = { ...DEFAULT_SECTION_BG, ...preset.map };
    saveMap(next);
    setMap(next);
    setActivePresetId(presetId);
    savePreset(presetId);
  }, []);

  const value = useMemo(
    () => ({
      map, sectionKey, currentBgId, setSectionBg, resetDefaults,
      sections: SECTIONS, backgrounds: BACKGROUNDS,
      presets: THEME_PRESETS, activePresetId, applyPreset,
    }),
    [map, sectionKey, currentBgId, setSectionBg, resetDefaults, activePresetId, applyPreset],
  );

  return (
    <BackgroundContext.Provider value={value}>
      {children}
    </BackgroundContext.Provider>
  );
}

export function useBackground() {
  const ctx = useContext(BackgroundContext);
  if (!ctx) throw new Error("useBackground must be used inside BackgroundProvider");
  return ctx;
}
